/**
 * Miscellaneous modules: services, billItems, public, notifications,
 * messages, sessions, commonChronicConditions, commonAllergies, followUp.
 */
import { fetchApi, API_BASE_URL } from '../client';
import { secureStorage } from '../../secureStorage';
import { csrfHeader } from '../../csrf';
import type {
  Service, BillItem, Notification, UnreadCountResponse,
  FollowUpDto, FollowUpStatsDto,
} from '../../../types';

export const servicesModule = {
  getAll: () => fetchApi<Service[]>('/api/services'),
  create: (data: Partial<Service>) =>
    fetchApi<Service>('/api/services', { method: 'POST', body: JSON.stringify(data) }),
};

export const billItemsModule = {
  create: (data: Partial<BillItem>) =>
    fetchApi<BillItem>('/api/bill-items', { method: 'POST', body: JSON.stringify(data) }),
  getByVisit: (visitId: string) =>
    fetchApi<BillItem[]>(`/api/bill-items/visit/${visitId}`),
};

export const publicModule = {
  getDoctors: (clinicId: string) =>
    fetchApi<any[]>(`/api/public/v1/qr-checkin/${clinicId}/doctors`, { skipAuth: true }),
  qrCheckin: (clinicId: string, data: any) =>
    fetchApi<any>(`/api/public/v1/qr-checkin/${clinicId}`, {
      method: 'POST',
      body: JSON.stringify(data),
      skipAuth: true,
    }),
};

export const notificationsModule = {
  getAll: async (limit?: number) => {
    const url = limit ? `/api/notifications?limit=${limit}` : '/api/notifications';
    const res = await fetchApi<Notification[]>(url);
    if (res.error) throw new Error(res.error);
    return res.data || [];
  },
  getUnread: async () => {
    const res = await fetchApi<Notification[]>('/api/notifications/unread');
    if (res.error) throw new Error(res.error);
    return res.data || [];
  },
  getUnreadCount: async () => {
    const res = await fetchApi<UnreadCountResponse>('/api/notifications/unread-count');
    if (res.error) throw new Error(res.error);
    return res.data!;
  },
  markAsRead: async (notificationId: string) => {
    const res = await fetchApi<void>(`/api/notifications/${notificationId}/mark-read`, { method: 'POST' });
    if (res.error) throw new Error(res.error);
  },
  delete: async (notificationId: string) => {
    const res = await fetchApi<void>(`/api/notifications/${notificationId}`, { method: 'DELETE' });
    if (res.error) throw new Error(res.error);
  },
  markAllAsRead: async () => {
    const res = await fetchApi<void>('/api/notifications/mark-all-read', { method: 'POST' });
    if (res.error) throw new Error(res.error);
  },
};

export const messagesModule = {
  send: async (data: { recipientId: string; subject: string; content: string; attachments?: File[] }) => {
    const formData = new FormData();
    formData.append('recipientId', data.recipientId);
    formData.append('subject', data.subject);
    formData.append('content', data.content);
    data.attachments?.forEach(file => formData.append('attachments', file));
    const response = await fetch(`${API_BASE_URL}/api/messages`, {
      method: 'POST',
      headers: { ...csrfHeader('POST') }, // cookie auth (credentials below) + CSRF token
      body: formData,
      credentials: 'include',
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to send message');
    }
    return response.json();
  },
  getInbox: async () => {
    const res = await fetchApi<any[]>('/api/messages/inbox');
    if (res.error) throw new Error(res.error);
    return res.data || [];
  },
  getSent: async () => {
    const res = await fetchApi<any[]>('/api/messages/sent');
    if (res.error) throw new Error(res.error);
    return res.data || [];
  },
  markAsRead: (id: string) => fetchApi(`/api/messages/${id}/read`, { method: 'POST' }),
  delete: (id: string) => fetchApi(`/api/messages/${id}`, { method: 'DELETE' }),
};

/** Doctor side of patient↔doctor messaging — backs the "Patient Messages" tab in Clinic Mail. */
export const patientMessagesModule = {
  getThreads: () =>
    fetchApi<Array<{
      patientId: string; patientName: string; lastSubject?: string; lastBody: string;
      lastSenderType: 'PATIENT' | 'DOCTOR'; lastMessageAt: string; unreadCount: number;
    }>>('/api/doctor/patient-messages/threads'),
  getThread: (patientId: string) =>
    fetchApi<Array<{
      id: string; senderType: 'PATIENT' | 'DOCTOR'; subject?: string; body: string;
      isRead: boolean; createdAt: string;
    }>>(`/api/doctor/patient-messages/thread/${patientId}`),
  reply: (patientId: string, data: { subject?: string; body: string }) =>
    fetchApi<{ id: string }>(`/api/doctor/patient-messages/thread/${patientId}`, {
      method: 'POST',
      body: JSON.stringify(data),
    }),
};

export const sessionsModule = {
  create: (payload: { patientId: string; clinicId?: string }) =>
    fetchApi<{ token: string; expires_in: number; expires_at: string }>('/api/sessions', {
      method: 'POST',
      body: JSON.stringify(payload),
    }),
  revoke: (token: string) =>
    fetchApi<void>(`/api/sessions/${token}`, { method: 'DELETE' }),
};

export const commonChronicConditionsModule = {
  getAll: () =>
    fetchApi<{ data: Array<{ id: string; conditionName: string; displayOrder: number }> }>(
      '/api/common-chronic-conditions'
    ),
};

export const commonAllergiesModule = {
  getAll: () =>
    fetchApi<{ data: Array<{ id: string; allergyName: string; displayOrder: number }> }>(
      '/api/common-allergies'
    ),
  createOrGet: (allergyName: string) =>
    fetchApi<{ data: { id: string; allergyName: string; displayOrder: number } }>(
      '/api/common-allergies',
      { method: 'POST', body: JSON.stringify({ allergyName }) }
    ),
};

export const followUpModule = {
  getQueue: (filterType?: 'TODAY' | 'UPCOMING' | 'OVERDUE') =>
    fetchApi<FollowUpDto[]>(
      filterType ? `/api/follow-ups/queue?filterType=${filterType}` : '/api/follow-ups/queue'
    ),
  getStats: () => fetchApi<FollowUpStatsDto>('/api/follow-ups/stats'),
  scheduleFollowUp: (followUpId: string, tokenId: string) =>
    fetchApi<FollowUpDto>(`/api/follow-ups/${followUpId}/schedule`, {
      method: 'POST',
      body: JSON.stringify({ tokenId }),
    }),
  cancelFollowUp: (followUpId: string) =>
    fetchApi<FollowUpDto>(`/api/follow-ups/${followUpId}/cancel`, { method: 'PATCH' }),
  getFollowUp: (followUpId: string) =>
    fetchApi<FollowUpDto>(`/api/follow-ups/${followUpId}`),
  /** WhatsApp (+ SMS/in-app) reminder — idempotent per follow-up on the backend, safe to click again. */
  sendReminder: (followUpId: string) =>
    fetchApi<{ sent: boolean; channelsAttempted: number; message: string }>(
      `/api/follow-ups/${followUpId}/send-reminder`, { method: 'POST' }
    ),
  getForBill: (billId: string) =>
    fetchApi<any>(`/api/follow-ups/bill/${billId}`),
  scheduleAppointment: (followUpId: string, appointmentSlotId: string) =>
    fetchApi<any>(`/api/follow-ups/${followUpId}/schedule-appointment`, {
      method: 'POST',
      body: JSON.stringify({ appointmentSlotId }),
    }),
};

export interface LabDto {
  id: string;
  clinicId: string;
  name: string;
  isInHouse: boolean;
  isActive: boolean;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  operatingHours?: string;
  website?: string;
  createdAt?: string;
  updatedAt?: string;
}

/** A clinic's preferred lab partner, with its resolved Lab details (from GET /clinic/preferred-labs). */
export interface PreferredLabDto {
  id: string;
  labId: string;
  displayOrder: number;
  active: boolean;
  name?: string;
  inHouse?: boolean;
  phone?: string;
  email?: string;
  address?: string;
  city?: string;
  state?: string;
  operatingHours?: string;
  website?: string;
}

export const labsModule = {
  getLabs: (activeOnly = true) =>
    fetchApi<LabDto[]>(`/api/clinic/labs?activeOnly=${activeOnly}`),
  createLab: (body: Partial<LabDto>) =>
    fetchApi<LabDto>('/api/clinic/labs', { method: 'POST', body: JSON.stringify(body) }),
  // Doctor-accessible: add an EXTERNAL referral lab on the fly while ordering. Always creates an
  // external lab (never in-house), deduped by name, and persists it to the clinic so it shows up in
  // the Send-to picker from then on.
  createExternalLab: (body: { name: string; phone?: string; city?: string; address?: string; state?: string; email?: string }) =>
    fetchApi<LabDto>('/api/clinic/labs/external', { method: 'POST', body: JSON.stringify(body) }),
  updateLab: (id: string, body: Partial<LabDto>) =>
    fetchApi<LabDto>(`/api/clinic/labs/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  deactivateLab: (id: string) =>
    fetchApi<{ message: string }>(`/api/clinic/labs/${id}`, { method: 'DELETE' }),
  assignLab: (prescriptionId: string, labId: string) =>
    fetchApi<any>(`/api/doctor/prescriptions/${prescriptionId}/lab-orders/assign-lab`, {
      method: 'POST',
      body: JSON.stringify({ labId }),
    }),

  /**
   * Fetch the printable Lab Requisition PDF for an accession as a Blob (authenticated). The caller
   * opens it for preview/print or triggers a download — the PDF is never a public/predictable URL.
   */
  fetchRequisition: async (accessionId: string): Promise<{ blob?: Blob; error?: string }> => {
    const token = secureStorage.getAuthToken();
    const headers: Record<string, string> = {};
    if (token) headers['Authorization'] = `Bearer ${token}`;
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/lab/accessions/${accessionId}/requisition`,
        { method: 'GET', headers, credentials: 'include' },
      );
      if (!response.ok) {
        if (response.status === 401) { secureStorage.removeAuthToken(); window.location.href = '/login'; return { error: 'Unauthorized' }; }
        return { error: 'Failed to generate the lab requisition PDF' };
      }
      return { blob: await response.blob() };
    } catch {
      return { error: 'Failed to generate the lab requisition PDF' };
    }
  },
};

/** Clinic's Preferred Laboratory Partners — the ordered list printed on lab requisitions. */
export const preferredLabsModule = {
  list: () => fetchApi<PreferredLabDto[]>('/api/clinic/preferred-labs'),
  add: (labId: string) =>
    fetchApi<PreferredLabDto>('/api/clinic/preferred-labs', { method: 'POST', body: JSON.stringify({ labId }) }),
  setActive: (id: string, active: boolean) =>
    fetchApi<PreferredLabDto>(`/api/clinic/preferred-labs/${id}`, { method: 'PATCH', body: JSON.stringify({ active }) }),
  reorder: (orderedIds: string[]) =>
    fetchApi<PreferredLabDto[]>('/api/clinic/preferred-labs/reorder', { method: 'PUT', body: JSON.stringify({ orderedIds }) }),
  remove: (id: string) =>
    fetchApi<{ message: string }>(`/api/clinic/preferred-labs/${id}`, { method: 'DELETE' }),
};
